import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Card,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  InputAdornment,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { QRCodeCanvas } from "qrcode.react";
import api from "../../utils/api";
import useCompanyLogo from "../../utils/useCompanyLogo";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const ShareTab = ({ election }) => {
  const logoUrl = useCompanyLogo();
  const [shareLink, setShareLink] = useState("");
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [memberSearch, setMemberSearch] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMemberCodes, setSelectedMemberCodes] = useState([]);

  const [customEmails, setCustomEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [copyLabel, setCopyLabel] = useState("Copy Link");

  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberModalQuery, setMemberModalQuery] = useState("");
  const [memberModalResults, setMemberModalResults] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const selectedSet = useMemo(
    () => new Set(selectedMemberCodes),
    [selectedMemberCodes]
  );

  const allVisibleSelected =
    memberModalResults.length > 0 &&
    memberModalResults.every((m) => selectedSet.has(m.ClubMemberCode));

  const someVisibleSelected =
    memberModalResults.some((m) => selectedSet.has(m.ClubMemberCode)) &&
    !allVisibleSelected;

  useEffect(() => {
    if (!election?.id) return;
    loadShareLink();
    loadStats();
  }, [election?.id]);

  const loadShareLink = async () => {
    try {
      const res = await api.get(`/elections/${election.id}/share-link`);
      setShareLink(res.data?.url || "");
    } catch (err) {
      console.error("Failed to load share link:", err);
    }
  };

  const loadStats = async () => {
    if (!election?.id) return;
    setLoadingStats(true);
    try {
      const res = await api.get(`/elections/${election.id}/share/stats`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load share stats:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  const searchMembers = async () => {
    if (!memberSearch.trim()) {
      setMemberResults([]);
      return;
    }
    try {
      const res = await api.get(
        `/members?search=${encodeURIComponent(memberSearch)}`
      );
      setMemberResults(res.data?.members || []);
    } catch (err) {
      console.error("Failed to search members:", err);
    }
  };

  const toggleMemberSelect = (memberCode) => {
    setSelectedMemberCodes((prev) =>
      prev.includes(memberCode)
        ? prev.filter((c) => c !== memberCode)
        : [...prev, memberCode]
    );
  };

  const fetchMembersForModal = async (query = "") => {
    setLoadingMembers(true);
    try {
      const res = await api.get(
        `/members?search=${encodeURIComponent(query)}&top=100`
      );
      setMemberModalResults(res.data?.members || []);
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (!memberModalOpen) return;
    fetchMembersForModal(memberModalQuery || "");
  }, [memberModalOpen]);

  const toggleOne = (code) => {
    setSelectedMemberCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const selectAllVisible = () => {
    const visibleCodes = memberModalResults
      .map((m) => m.ClubMemberCode)
      .filter(Boolean);
    setSelectedMemberCodes((prev) => Array.from(new Set([...prev, ...visibleCodes])));
  };

  const clearAllVisible = () => {
    const visibleCodes = new Set(
      memberModalResults.map((m) => m.ClubMemberCode)
    );
    setSelectedMemberCodes((prev) => prev.filter((c) => !visibleCodes.has(c)));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopyLabel("Copied!");
      setTimeout(() => setCopyLabel("Copy Link"), 2000);
    } catch (err) {
      console.error("Clipboard error:", err);
    }
  };

  const handleSendInvites = async () => {
    if (!election?.id) return;

    const emails = customEmails.split(",").map((e) => e.trim()).filter(Boolean);
    const totalCount = selectedMemberCodes.length + emails.length;

    // Confirmation dialog before bulk send
    if (totalCount === 0) {
      setSnack({ open:true, message:"Select at least one member or enter an email address.", severity:"warning" });
      return;
    }
    const confirmed = window.confirm(
      `You are about to send ${totalCount} invitation${totalCount!==1?"s":""}:\n` +
      `• ${selectedMemberCodes.length} member(s) by code\n` +
      `• ${emails.length} custom email(s)\n\n` +
      `This cannot be undone. Continue?`
    );
    if (!confirmed) return;

    setSending(true);
    try {
      await api.post(`/elections/${election.id}/share/send`, {
        memberCodes: selectedMemberCodes,
        emails,
      });

      setCustomEmails("");
      setSelectedMemberCodes([]);
      await loadStats();
      setSnack({ open: true, message: `${totalCount} invitation${totalCount!==1?"s":""} sent successfully!`, severity: "success" });
    } catch (err) {
      console.error("Failed to send invites:", err);
      setSnack({ open: true, message: err.response?.data?.message || "Failed to send invitations. Check SMTP configuration.", severity: "error" });
    } finally {
      setSending(false);
    }
  };

  const downloadQr = () => {
    const canvas = document.querySelector("canvas");
    if (!canvas) return;

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `Election_${election.id}_QRCode.png`;
    downloadLink.click();
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(340px, 1fr)" },
        gap: 2.5,
        alignItems: "stretch",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <LinkIcon sx={{ mr: 1, color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Share Link
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Anyone with this link can access the ballot after logging in.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={shareLink}
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />

            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              sx={{
                whiteSpace: "nowrap",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              {copyLabel}
            </Button>
          </Box>
        </Card>

        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <MailOutlineIcon sx={{ mr: 1, color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Send Invitations
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Invite specific members by member code, plus any extra email addresses.
          </Typography>

          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 1.25,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                label="Search Members (name, code, address...)"
                fullWidth
                size="small"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchMembers()}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />

              <Button
                variant="outlined"
                onClick={() => setMemberModalOpen(true)}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
                startIcon={<GroupAddIcon />}
                fullWidth
              >
                Select Members
              </Button>
            </Box>

            <Box sx={{ maxHeight: 150, overflowY: "auto" }}>
              {memberResults.map((m) => {
                const code = m.ClubMemberCode;
                const label = `${m.LastName}, ${m.FirstName} (${code})`;
                const selected = selectedMemberCodes.includes(code);
                return (
                  <Chip
                    key={code}
                    label={label}
                    color={selected ? "primary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    size="small"
                    onClick={() => toggleMemberSelect(code)}
                    sx={{
                      mr: 1,
                      mb: 1,
                      borderRadius: "999px",
                      fontWeight: 600,
                    }}
                  />
                );
              })}
            </Box>

            {selectedMemberCodes.length > 0 && (
              <Typography variant="caption" sx={{ mt: 1, display: "block", color: "#6b778c" }}>
                Selected members: {selectedMemberCodes.length}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <TextField
            label="Additional email addresses (comma separated)"
            fullWidth
            size="small"
            value={customEmails}
            onChange={(e) => setCustomEmails(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
              },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={sending}
              onClick={handleSendInvites}
              sx={{
                backgroundColor: "#2f63ad",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#275592",
                  boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                },
              }}
            >
              {sending ? "Sending..." : "Send Invitations"}
            </Button>
          </Box>
        </Card>

        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <QrCode2Icon sx={{ mr: 1, color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              QR Code
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Scan or share this QR code to open the ballot.
          </Typography>

          <Box sx={{ textAlign: "center", p: 2 }}>
            {shareLink ? (
              <QRCodeCanvas
                value={shareLink}
                size={180}
                includeMargin
                {...(logoUrl ? {
                  imageSettings: {
                    src: logoUrl,
                    height: 36,
                    width: 36,
                    excavate: true,
                  }
                } : {})}
              />
            ) : (
              <Typography sx={{ color: "#6b778c" }}>No link yet</Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="outlined"
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
              onClick={downloadQr}
            >
              Download QR
            </Button>
          </Box>
        </Card>
      </Box>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          height: "100%",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Invitation Status
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Track sent invites, opens, votes, and public link activity.
            </Typography>
          </Box>

          <Tooltip title="Refresh stats">
            <IconButton
              onClick={loadStats}
              sx={{
                borderRadius: "10px",
                border: "1px solid rgba(47,99,173,0.12)",
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            height: "calc(100% - 88px)",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {loadingStats ? (
            <Typography sx={{ color: "#6b778c" }}>Loading stats...</Typography>
          ) : !stats ? (
            <Typography sx={{ color: "#6b778c" }}>No invitations yet.</Typography>
          ) : (
            <>
              <Box sx={{ flex: "0 0 auto" }}>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                  <Chip label={`Invites: ${stats?.invites?.total || 0}`} />
                  <Chip
                    label={`Sent: ${stats?.invites?.sent || 0}`}
                    sx={{
                      fontWeight: 700,
                      color: "#2563eb",
                      backgroundColor: "rgba(59,130,246,0.12)",
                    }}
                  />
                  <Chip
                    label={`Opened: ${stats?.invites?.opened || 0}`}
                    sx={{
                      fontWeight: 700,
                      color: "#b45309",
                      backgroundColor: "rgba(245,158,11,0.12)",
                    }}
                  />
                  <Chip
                    label={`Voted: ${stats?.invites?.voted || 0}`}
                    sx={{
                      fontWeight: 700,
                      color: "#15803d",
                      backgroundColor: "rgba(34,197,94,0.12)",
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                  <Chip label={`Public link opens: ${stats?.publicLink?.opens || 0}`} />
                  {stats?.publicLink?.expiresAt && (
                    <Chip
                      variant="outlined"
                      label={`Expires: ${new Date(
                        stats.publicLink.expiresAt
                      ).toLocaleDateString()}`}
                    />
                  )}
                </Stack>

                <Divider sx={{ mb: 2 }} />
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, minHeight: 0 }}>
                {(stats?.inviteList || []).map((inv) => (
                  <Box
                    key={inv.Id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.1,
                      gap: 2,
                      borderBottom: "1px solid rgba(47,99,173,0.06)",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#223142" }}>
                        {inv.Email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b778c" }}>
                        {inv.InviteType === "member"
                          ? `Member: ${inv.MemberCode}`
                          : "Generic invite"}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={inv.Status}
                      color={
                        inv.Status === "voted"
                          ? "success"
                          : inv.Status === "opened"
                          ? "warning"
                          : inv.Status === "sent"
                          ? "info"
                          : "default"
                      }
                    />
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Card>

      <Dialog
        open={memberModalOpen}
        onClose={() => setMemberModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            boxShadow: "0 18px 60px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          Select Members
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search members (name, code, address...)"
              value={memberModalQuery}
              onChange={(e) => setMemberModalQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchMembersForModal(memberModalQuery);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={() => fetchMembersForModal(memberModalQuery)}
              sx={{
                borderRadius: "10px",
                whiteSpace: "nowrap",
                textTransform: "none",
                fontWeight: 700,
              }}
              disabled={loadingMembers}
            >
              {loadingMembers ? "Searching..." : "Search"}
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 1,
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Typography variant="caption" sx={{ color: "#6b778c" }}>
              {memberModalResults.length} results • {selectedMemberCodes.length} selected
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={selectAllVisible}
                disabled={!memberModalResults.length}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Select all
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={clearAllVisible}
                disabled={!memberModalResults.length}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 720,
                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "rgba(47,99,173,0.04)",
                  color: "#223142",
                  fontWeight: 700,
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": {
                  backgroundColor: "rgba(47,99,173,0.025)",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected}
                      onChange={(e) =>
                        e.target.checked ? selectAllVisible() : clearAllVisible()
                      }
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Member Code</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {memberModalResults.map((m) => {
                  const code = m.ClubMemberCode;
                  const name =
                    `${m.LastName || ""}${m.FirstName ? `, ${m.FirstName}` : ""}`.trim() ||
                    "N/A";
                  const email = m.EmailAddress || "N/A";
                  const checked = !!code && selectedSet.has(code);

                  return (
                    <TableRow
                      key={code || `${name}-${email}`}
                      hover
                      onClick={() => code && toggleOne(code)}
                      sx={{ cursor: code ? "pointer" : "default" }}
                    >
                      <TableCell
                        padding="checkbox"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={!code}
                          onChange={() => code && toggleOne(code)}
                        />
                      </TableCell>
                      <TableCell>{name}</TableCell>
                      <TableCell>{code || "N/A"}</TableCell>
                      <TableCell>{email}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setSelectedMemberCodes([]);
            }}
            sx={{
              mr: "auto",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Clear All Selected
          </Button>

          <Button
            variant="outlined"
            onClick={() => setMemberModalOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: "12px" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShareTab;
